import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FileText, TrendingUp, Target, MessageSquare, Users, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "Mental Training Plan", path: "/plans" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: Users, label: "Teams", path: "/teams" },
  { icon: MessageSquare, label: "Connect", path: "/messages" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" }
];

export default function Teams({ user, onLogout }) {
  const [teams, setTeams] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("athlete");
  const [myInvites, setMyInvites] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });

  useEffect(() => {
    fetchTeams();
    fetchMyInvites();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchMessages(selectedTeam.id);
      const interval = setInterval(() => fetchMessages(selectedTeam.id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      toast.error("Failed to load teams");
    }
  };

  const fetchMyInvites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/my-invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyInvites(response.data);
    } catch (error) {
      console.error("Failed to load invites:", error);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamDetails(response.data);
    } catch (error) {
      toast.error("Failed to load team details");
    }
  };

  const fetchMessages = async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const handleCreateTeam = async () => {
    if (!formData.name) {
      toast.error("Team name is required");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teams`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Team created!");
      setShowCreate(false);
      setFormData({ name: "", description: "" });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error("Email is required");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teams/${selectedTeam.id}/invite`, {
        team_id: selectedTeam.id,
        email: inviteEmail,
        role: inviteRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Invite sent to ${inviteEmail}!`);
      setShowInvite(false);
      setInviteEmail("");
      setInviteRole("athlete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    }
  };

  const handleAcceptInvite = async (teamId, email) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teams/accept-invite`, {
        team_id: teamId,
        invite_email: email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Team invite accepted!");
      fetchTeams();
      fetchMyInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept invite");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/teams/${selectedTeam.id}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Member removed");
      fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages`, {
        team_id: selectedTeam.id,
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage("");
      fetchMessages(selectedTeam.id);
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Teams
            </h1>
            <p className="text-lg text-gray-600">
              Collaborate with coaches and teammates
            </p>
          </div>
          {user.role === 'team-admin' && (
            <Button
              data-testid="create-team-btn"
              onClick={() => setShowCreate(true)}
              className="bg-sky-600 hover:bg-sky-700"
            >
              Create Team
            </Button>
          )}
        </div>

        {/* Pending Invites */}
        {myInvites.length > 0 && (
          <div className="mb-8">
            <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="text-xl text-green-900">Team Invitations</CardTitle>
                <CardDescription className="text-green-700">
                  You have {myInvites.length} pending team {myInvites.length === 1 ? 'invitation' : 'invitations'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {myInvites.map((invite, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-4 rounded-lg border border-green-200">
                      <div>
                        <p className="font-semibold text-gray-900">{invite.team_name}</p>
                        <p className="text-sm text-gray-600">Role: {invite.role}</p>
                      </div>
                      <Button
                        data-testid={`accept-invite-${index}`}
                        onClick={() => handleAcceptInvite(invite.team_id, invite.invited_at)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Accept
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Teams Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((team) => (
            <Card
              key={team.id}
              data-testid={`team-card-${team.id}`}
              className="border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:shadow-xl transition-all duration-300"
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <Users className="w-10 h-10 text-sky-600" />
                  {team.admin_id === user.id && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                      Administrator
                    </span>
                  )}
                </div>
                <CardTitle className="text-xl">{team.name}</CardTitle>
                {team.description && (
                  <CardDescription className="text-base">{team.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600 mb-4">
                  {(team.athlete_ids?.length || 0) + (team.coach_ids?.length || 0) + (team.parent_ids?.length || 0)} total members
                </div>
                <div className="flex space-x-2">
                  <Button
                    data-testid={`view-team-${team.id}`}
                    onClick={() => {
                      setSelectedTeam(team);
                      fetchTeamDetails(team.id);
                      if (team.admin_id === user.id) {
                        setShowMembers(true);
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    {team.admin_id === user.id ? 'Manage' : 'View'}
                  </Button>
                  <Button
                    data-testid={`chat-team-${team.id}`}
                    onClick={() => setSelectedTeam(team)}
                    size="sm"
                    className="flex-1 bg-sky-600 hover:bg-sky-700"
                  >
                    Chat
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {teams.length === 0 && (
            <div className="col-span-3 text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                {user.role === 'coach' 
                  ? "No teams yet. Create your first team!"
                  : "You're not part of any teams yet"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Team Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-team-dialog">
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription>Set up a team to collaborate with athletes</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Team Name *</Label>
              <Input
                data-testid="team-name-input"
                id="name"
                placeholder="e.g., Varsity Basketball"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                data-testid="team-description-textarea"
                id="description"
                placeholder="Brief description of the team"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <Button
              data-testid="submit-team-btn"
              onClick={handleCreateTeam}
              className="w-full bg-sky-600 hover:bg-sky-700"
            >
              Create Team
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Members Management Dialog */}
      <Dialog open={showMembers} onOpenChange={setShowMembers}>
        <DialogContent data-testid="team-members-dialog" className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedTeam?.name} - Team Management</DialogTitle>
            <DialogDescription>Manage team members and send invitations</DialogDescription>
          </DialogHeader>
          
          {teamDetails && (
            <div className="space-y-6 py-4">
              {/* Invite Section */}
              <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">Invite New Members</h3>
                  <Button
                    data-testid="send-invite-btn"
                    onClick={() => setShowInvite(true)}
                    size="sm"
                    className="bg-sky-600 hover:bg-sky-700"
                  >
                    Send Invite
                  </Button>
                </div>
                <p className="text-sm text-gray-600">
                  Send email invitations to athletes, parents, or coaches to join your team
                </p>
              </div>

              {/* Current Members */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Team Members ({teamDetails.members?.length || 0})</h3>
                <div className="space-y-3">
                  {teamDetails.members?.map((member) => (
                    <div
                      key={member.id}
                      data-testid={`member-${member.id}`}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-600">{member.email}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          member.role === 'athlete' ? 'bg-blue-100 text-blue-800' :
                          member.role === 'coach' ? 'bg-green-100 text-green-800' :
                          member.role === 'parent' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.role}
                        </span>
                        {teamDetails.admin_id === user.id && member.id !== user.id && (
                          <Button
                            data-testid={`remove-member-${member.id}`}
                            onClick={() => handleRemoveMember(member.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!teamDetails.members || teamDetails.members.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No members yet. Start by inviting people!</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="send-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite to Team</DialogTitle>
            <DialogDescription>Send an invitation to join {selectedTeam?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="invite-email">Email Address *</Label>
              <Input
                data-testid="invite-email-input"
                id="invite-email"
                type="email"
                placeholder="member@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="invite-role">Role *</Label>
              <select
                data-testid="invite-role-select"
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-white"
              >
                <option value="athlete">Athlete</option>
                <option value="parent">Parent</option>
                <option value="coach">Coach</option>
              </select>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                📧 An invitation will be sent to this email. They can register with this email address to automatically join the team.
              </p>
            </div>
            <Button
              data-testid="submit-invite-btn"
              onClick={handleSendInvite}
              className="w-full bg-sky-600 hover:bg-sky-700"
            >
              Send Invitation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Chat Dialog */}
      <Dialog open={!!selectedTeam && !showMembers} onOpenChange={() => setSelectedTeam(null)}>
        <DialogContent data-testid="team-chat-dialog" className="max-w-3xl h-[600px] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedTeam?.name}</DialogTitle>
            <DialogDescription>{selectedTeam?.description}</DialogDescription>
          </DialogHeader>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-3 py-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                data-testid={`team-message-${index}`}
                className={`flex ${msg.user_id === user.id ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    msg.user_id === user.id
                      ? 'bg-sky-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {msg.user_id !== user.id && (
                    <p className="text-xs font-semibold mb-1 opacity-70">{msg.user_name}</p>
                  )}
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${msg.user_id === user.id ? 'text-sky-100' : 'text-gray-500'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            {messages.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No messages yet. Start the conversation!
              </p>
            )}
          </div>

          {/* Input */}
          <div className="flex space-x-2 pt-4 border-t">
            <Input
              data-testid="team-message-input"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            <Button
              data-testid="send-team-message-btn"
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              className="bg-sky-600 hover:bg-sky-700"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}